import React, { useState } from "react";
import {
  Box,
  Flex,
  Grid,
  Skeleton,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import CustomIcon from "../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { PathPickMarket } from "@/features/pathpick/types";
import {
  PathPickOptionGroupKey,
  PathPickOptionMarketCell,
  PathPickProductOptionRow,
  PathPickProductOptionTableModel,
} from "@/features/pathpick/utils/productOptions";
import { PATH_PICK_MARKET_LABEL } from "@/features/pathpick/utils/market";

export interface PathPickOptionMarginTarget {
  market: PathPickMarket;
  basisValue: number | null;
  expectedMargin: number | null;
  marginRate: number | null;
  optionLabel: string;
}

interface Props {
  model: PathPickProductOptionTableModel;
  // 사이즈별 공급가 데이터가 없어 상품 레벨 값을 모든 행에 반복한다.
  supplyPrice: number;
  // 비교 표시를 끄면 마켓 칸을 아예 만들지 않는다 — 옵션명·재고·공급가만 남는다.
  isComparisonVisible: boolean;
  // 두 마켓 옵션 중 하나라도 조회 중이면 본문을 스켈레톤으로 둔다 —
  // 먼저 온 쪽만 그리면 아직 모르는 마켓이 "미매칭"으로 단정돼 보인다.
  isPending: boolean;
  // 조회 중일 때 그릴 행 수(= 패스레이드 옵션 수)
  pendingRowCount: number;
  onOpenMarginFormula: (target: PathPickOptionMarginTarget) => void;
}

// 옵션명 / 매칭 상태 / 구매가능재고 / 공급가 / 마켓 / 최저 입찰가 / 거래가 / 거래수 / 예상 마진 / 추천
const TABLE_COLUMNS = "96px 104px 88px 104px 64px 1fr 1fr 72px 1fr 148px";
const TABLE_MIN_WIDTH = "1180px";

// 비교를 끈 표 — 옵션명 / 구매가능재고 / 공급가. 열이 셋뿐이라 가로 스크롤 없이 모달 폭을 채운다.
const TABLE_COLUMNS_WITHOUT_COMPARISON = "1fr 1fr 1fr";

// 그룹별 색 테마 — 패스레이드(초록) / POIZON(노랑) / KREAM(파랑).
// 마켓 전용 그룹의 색은 해당 마켓 색을 그대로 따른다(어느 마켓 얘기인지 색으로 먼저 읽힌다).
const GROUP_THEMES: Record<
  PathPickOptionGroupKey,
  { color: string; borderColor: string; background: string }
> = {
  pathrade: {
    color: "#16B41C",
    borderColor: "#A9E0AB",
    background: "#F0FAEF",
  },
  poizon: { color: "#B7791F", borderColor: "#F0DFAE", background: "#FFF9E8" },
  kream: { color: "#1272EF", borderColor: "#A8CEFA", background: "#F0F7FF" },
};

// 매칭 상태 칩의 중립색 — 매칭이 아닌 경우(한쪽만/양쪽 미매칭)에 쓴다.
const NEUTRAL_CHIP = {
  color: "#8F8F8C",
  borderColor: "#D4D4D1",
  background: "#FFFFFF",
};

const GROUP_TITLE_KEY: Record<PathPickOptionGroupKey, string> = {
  pathrade: "path_pick:search_products.size_stock_modal.group_pathrade",
  poizon: "path_pick:search_products.size_stock_modal.group_poizon",
  kream: "path_pick:search_products.size_stock_modal.group_kream",
};

interface OptionChipProps {
  label: string;
  color: string;
  borderColor: string;
  background: string;
}

const OptionChip = ({
  label,
  color,
  borderColor,
  background,
}: OptionChipProps) => (
  <Flex
    width={"fit-content"}
    alignItems={"center"}
    padding={"1px 8px"}
    borderRadius={"4px"}
    border={`0.5px solid ${borderColor}`}
    background={background}
  >
    <Text textStyle={"caption-bold"} color={color} whiteSpace={"nowrap"}>
      {label}
    </Text>
  </Flex>
);

const HEADER_KEYS = [
  "path_pick:search_products.size_stock_modal.table_option",
  "path_pick:search_products.size_stock_modal.table_match_state",
  "path_pick:search_products.size_stock_modal.table_stock",
  "path_pick:search_products.size_stock_modal.table_supply_price",
  "path_pick:search_products.size_stock_modal.table_market",
  "path_pick:search_products.size_stock_modal.table_lowest_price",
  "path_pick:search_products.size_stock_modal.table_last_price",
  "path_pick:search_products.size_stock_modal.table_sales_count",
  "path_pick:search_products.product_list.expected_margin",
  "path_pick:search_products.size_stock_modal.table_recommendation",
];

// 매칭 상태는 "어느 마켓에 붙었는가"를 말하는 칸이라 비교와 함께 빠진다.
const HEADER_KEYS_WITHOUT_COMPARISON = [
  "path_pick:search_products.size_stock_modal.table_option",
  "path_pick:search_products.size_stock_modal.table_stock",
  "path_pick:search_products.size_stock_modal.table_supply_price",
];

// 스켈레톤 셀 폭 — 칼럼 순서대로 실제 값 길이를 흉내낸다.
const SKELETON_CELL_WIDTHS = [
  "48px",
  "72px",
  "28px",
  "72px",
  "48px",
  "72px",
  "72px",
  "36px",
  "72px",
  "96px",
];

const SKELETON_CELL_WIDTHS_WITHOUT_COMPARISON = ["48px", "28px", "72px"];

interface OptionRowsSkeletonProps {
  rowCount: number;
  columns: string;
  cellWidths: string[];
}

const OptionRowsSkeleton = ({
  rowCount,
  columns,
  cellWidths,
}: OptionRowsSkeletonProps) => {
  // 옵션이 없는 상품은 응답 후에도 프레임만 남는다 — 스켈레톤도 같은 모양으로 둔다.
  if (rowCount === 0) {
    return null;
  }

  return (
    <>
      <Flex
        padding={"8px 16px"}
        borderTop={"0.5px solid #D4D4D1"}
        background={"#F7F9FA"}
      >
        <Skeleton height={"20px"} width={"104px"} />
      </Flex>
      {Array.from({ length: rowCount }).map((_, rowIndex) => (
        <Grid
          key={`OptionRowsSkeleton_${rowIndex}`}
          templateColumns={columns}
          gap={"8px"}
          padding={"12px 16px"}
          borderTop={"0.5px solid #D4D4D1"}
          alignItems={"center"}
        >
          {cellWidths.map((width, cellIndex) => (
            <Skeleton
              key={`OptionRowsSkeletonCell_${cellIndex}`}
              height={"20px"}
              width={width}
            />
          ))}
        </Grid>
      ))}
    </>
  );
};

// 모바일 카드의 지표 표 — 지표 라벨 / KREAM / POIZON.
// 데스크톱은 지표를 가로로 펼쳐 1180px이 필요하지만, 축을 뒤집으면 마켓 2칸만 남아 좁은 폭에 들어간다.
const MOBILE_METRIC_COLUMNS = "auto 1fr 1fr";

// 모바일 카드 스켈레톤의 지표 줄 수 — 마켓 헤더 + 최저 입찰가 / 거래가 / 거래수 / 예상 마진
const MOBILE_SKELETON_METRIC_ROW_COUNT = 5;

const OptionCardsSkeleton = ({ rowCount }: { rowCount: number }) => {
  // 표 스켈레톤과 같은 이유로 옵션이 없으면 아무것도 그리지 않는다.
  if (rowCount === 0) {
    return null;
  }

  return (
    <>
      <Flex
        padding={"8px 12px"}
        borderTop={"0.5px solid #D4D4D1"}
        background={"#F7F9FA"}
      >
        <Skeleton height={"20px"} width={"104px"} />
      </Flex>
      {Array.from({ length: rowCount }).map((_, rowIndex) => (
        <Flex
          key={`OptionCardsSkeleton_${rowIndex}`}
          flexDirection={"column"}
          gap={"10px"}
          padding={"12px"}
          borderTop={"0.5px solid #D4D4D1"}
        >
          <Flex justifyContent={"space-between"}>
            <Skeleton height={"20px"} width={"96px"} />
            <Skeleton height={"20px"} width={"48px"} />
          </Flex>
          <Skeleton height={"18px"} width={"120px"} />
          <Grid templateColumns={MOBILE_METRIC_COLUMNS} gap={"8px 12px"}>
            {Array.from({ length: MOBILE_SKELETON_METRIC_ROW_COUNT }).map(
              (_, metricIndex) => (
                <React.Fragment
                  key={`OptionCardsSkeletonMetric_${metricIndex}`}
                >
                  <Skeleton height={"18px"} width={"64px"} />
                  <Skeleton height={"18px"} width={"72px"} />
                  <Skeleton height={"18px"} width={"72px"} />
                </React.Fragment>
              )
            )}
          </Grid>
        </Flex>
      ))}
    </>
  );
};

/**
 * 옵션별 마켓 비교 표.
 *
 * 옵션 1건이 KREAM/POIZON 두 하위 행으로 나뉘고, 옵션 자체의 값(옵션명·매칭 상태·재고·공급가)과
 * 추천은 두 행에 걸쳐 한 칸으로 합쳐진다. 최저 입찰가와 거래가는 **둘 다** 보여주고,
 * 선택한 비교 기준을 따르는 것은 예상 마진뿐이다.
 *
 * 좁은 폭에서는 같은 값을 옵션 1건 = 카드 1장으로 그린다 — 지표를 세로로, 마켓을 가로로 뒤집어
 * 가로 스크롤 없이 한 옵션의 두 마켓 값을 나란히 읽게 한다.
 */
function PathPickProductOptionTable({
  model,
  supplyPrice,
  isComparisonVisible,
  isPending,
  pendingRowCount,
  onOpenMarginFormula,
}: Props) {
  const { t, tNumberString, tCurrencyString } = useI18nHelper();
  // 마켓 전용 옵션은 주문할 수 없는 참고 정보라 접어 둔다 — 매칭된 옵션을 먼저 읽게 하려는 것이다.
  // 호출부가 모달을 조건부로 마운트하므로 다시 열면 접힌 상태로 돌아온다.
  const [expandedGroups, setExpandedGroups] = useState<
    Partial<Record<PathPickOptionGroupKey, boolean>>
  >({});
  // 가로 스크롤은 뷰포트 폭 문제라 UA가 아니라 브레이크포인트로 가른다.
  const isNarrow = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );
  // 비교를 끈 표는 세 칸뿐이라 좁은 폭에서도 표 그대로 둔다.
  const isCardLayout = isComparisonVisible && Boolean(isNarrow);

  // 표의 골격은 비교 표시 여부로만 갈린다 — 헤더·스켈레톤·행이 같은 값을 보게 한 곳에서 고른다.
  const tableColumns = isComparisonVisible
    ? TABLE_COLUMNS
    : TABLE_COLUMNS_WITHOUT_COMPARISON;
  const headerKeys = isComparisonVisible
    ? HEADER_KEYS
    : HEADER_KEYS_WITHOUT_COMPARISON;
  const skeletonCellWidths = isComparisonVisible
    ? SKELETON_CELL_WIDTHS
    : SKELETON_CELL_WIDTHS_WITHOUT_COMPARISON;

  const formatKrw = (value?: number | null) =>
    value != null
      ? tCurrencyString(value, { type: "SYMBOL", code: "KRW" })
      : "-";

  const getMatchStateChip = (row: PathPickProductOptionRow) => {
    // 마켓 전용 그룹의 행은 공급처 옵션과 짝이 없다 — 그룹 색으로 "미매칭"만 알린다.
    if (row.group !== "pathrade") {
      return {
        label: t("path_pick:search_products.size_stock_modal.state_unmatched"),
        ...GROUP_THEMES[row.group],
      };
    }
    if (row.matchState === "matched") {
      return {
        label: t("path_pick:search_products.size_stock_modal.state_matched"),
        ...GROUP_THEMES.pathrade,
      };
    }
    if (row.matchState === "unmatched") {
      return {
        label: t("path_pick:search_products.size_stock_modal.state_unmatched"),
        ...NEUTRAL_CHIP,
      };
    }
    return {
      label: t(
        "path_pick:search_products.size_stock_modal.state_only_matched",
        {
          market:
            PATH_PICK_MARKET_LABEL[
              row.matchState === "kreamOnly" ? "KREAM" : "POIZON"
            ],
        }
      ),
      ...NEUTRAL_CHIP,
    };
  };

  // 값이 없는 칸의 문구 — 공급처 옵션 행에서는 "그 마켓에 이 사이즈가 없다"는 사실을 말해야 해서
  // "미매칭"으로 적고, 마켓 전용 행에서는 반대쪽 마켓을 굳이 판정하지 않고 "-"로 비운다.
  const getEmptyPriceText = (row: PathPickProductOptionRow) =>
    row.group === "pathrade"
      ? t("path_pick:search_products.size_stock_modal.state_unmatched")
      : "-";

  const renderRecommendation = (row: PathPickProductOptionRow) => {
    if (row.group !== "pathrade") {
      return (
        <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
          {t("path_pick:search_products.size_stock_modal.state_unmatched")}
        </Text>
      );
    }
    if (row.recommendation) {
      return (
        <Flex flexDirection={"column"} gap={"2px"}>
          <Text textStyle={"body-sm-bold"} color={"#1272EF"}>
            {t("path_pick:market.recommended", {
              market: PATH_PICK_MARKET_LABEL[row.recommendation.market],
            })}
          </Text>
          {row.recommendation.diff > 0 && (
            <Text textStyle={"caption-medium"} color={"#1272EF"}>
              {t("path_pick:market.recommended_margin_diff", {
                amount: formatKrw(row.recommendation.diff),
              })}
            </Text>
          )}
        </Flex>
      );
    }
    // 비교를 막은 쪽은 "매칭이 안 된 마켓"이 아니라 "마진을 못 낸 마켓"이다 —
    // 매칭됐어도 고른 기준의 시세가 없으면 마진이 비고, 그때도 비교할 수 없다.
    const blockingMarkets = [row.kream, row.poizon].filter(
      (cell) => cell.expectedMargin == null
    );
    return (
      <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
        {blockingMarkets.length === 1
          ? t("path_pick:market.compare_unavailable_by", {
              market: PATH_PICK_MARKET_LABEL[blockingMarkets[0].market],
            })
          : t("path_pick:market.compare_unavailable")}
      </Text>
    );
  };

  const formatStock = (stock: number | null) => {
    if (stock === null) {
      return "-";
    }
    return stock === -1 ? "99개" : `${tNumberString(stock)}개`;
  };

  // 지표별 값 칸 — 표(가로)와 카드(세로)가 같은 함수를 써서 색·빈 칸 문구·마진 클릭 규칙을 공유한다.
  const renderLowestPrice = (
    row: PathPickProductOptionRow,
    cell: PathPickOptionMarketCell
  ) => (
    <Text
      textStyle={"body-sm-bold"}
      color={cell.lowestValue != null ? "#1272EF" : "#8F8F8C"}
    >
      {cell.isMatched ? formatKrw(cell.lowestValue) : getEmptyPriceText(row)}
    </Text>
  );

  const renderLastPrice = (
    row: PathPickProductOptionRow,
    cell: PathPickOptionMarketCell
  ) => (
    <Text
      textStyle={"body-sm-bold"}
      color={cell.lastValue != null ? "#1272EF" : "#8F8F8C"}
    >
      {cell.isMatched ? formatKrw(cell.lastValue) : getEmptyPriceText(row)}
    </Text>
  );

  const renderSalesCount = (cell: PathPickOptionMarketCell) => (
    <Text textStyle={"body-sm-bold"} color={"#444440"}>
      {cell.salesCount != null
        ? t("path_pick:search_products.popular.bid_count_value", {
            value: tNumberString(cell.salesCount),
          })
        : "-"}
    </Text>
  );

  const renderMargin = (
    row: PathPickProductOptionRow,
    cell: PathPickOptionMarketCell
  ) =>
    cell.expectedMargin != null ? (
      <Box
        width={"fit-content"}
        cursor={"pointer"}
        color={cell.expectedMargin < 0 ? "#CC1D1D" : "#16B41C"}
        onClick={() =>
          onOpenMarginFormula({
            market: cell.market,
            basisValue: cell.basisValue,
            expectedMargin: cell.expectedMargin,
            marginRate: cell.marginRate,
            optionLabel: row.label,
          })
        }
      >
        <Text textStyle={"body-sm-bold"} textDecoration={"underline"}>
          {formatKrw(cell.expectedMargin)}
        </Text>
        {cell.marginRate != null && (
          <Text textStyle={"caption-medium"} textDecoration={"underline"}>
            {`${cell.marginRate.toFixed(1)}%`}
          </Text>
        )}
      </Box>
    ) : (
      <Text textStyle={"body-sm-bold"} color={"#8F8F8C"}>
        {/* 매칭됐는데 마진만 막힌 경우(크림 체결수 부족)는 왜 비었는지 알려 준다.
                미매칭 셀은 위 가격 칸이 이미 "미매칭"을 말하므로 "-"로 둔다. */}
        {cell.isMatched && !cell.isMarginAvailable
          ? t(
              "path_pick:search_products.product_list.card.margin_calc_unavailable"
            )
          : "-"}
      </Text>
    );

  const renderMarketCells = (
    row: PathPickProductOptionRow,
    cell: PathPickOptionMarketCell
  ) => (
    <>
      <Text textStyle={"body-sm-medium"} color={"#444440"}>
        {PATH_PICK_MARKET_LABEL[cell.market]}
      </Text>
      {renderLowestPrice(row, cell)}
      {renderLastPrice(row, cell)}
      {renderSalesCount(cell)}
      {renderMargin(row, cell)}
    </>
  );

  const renderMetricLabel = (key: string) => (
    <Text textStyle={"caption-medium"} color={"#8F8F8C"} whiteSpace={"nowrap"}>
      {t(key)}
    </Text>
  );

  // 모바일 카드 — 옵션 단위 값(옵션명·매칭·재고·공급가)을 위에, 마켓 지표를 KREAM | POIZON 두 칸으로,
  // 추천을 맨 아래에 둔다. 마켓 칸 순서는 데스크톱 하위 행 순서(KREAM → POIZON)를 따른다.
  const renderOptionCard = (row: PathPickProductOptionRow) => {
    const chip = getMatchStateChip(row);
    const cells = [row.kream, row.poizon];

    return (
      <Flex
        key={row.key}
        flexDirection={"column"}
        gap={"10px"}
        padding={"12px"}
        borderTop={"0.5px solid #D4D4D1"}
      >
        <Flex
          justifyContent={"space-between"}
          alignItems={"center"}
          gap={"8px"}
        >
          <Flex alignItems={"center"} gap={"8px"} minWidth={0}>
            <Text textStyle={"body-md-bold"} color={"#444440"}>
              {row.label}
            </Text>
            <OptionChip
              label={chip.label}
              color={chip.color}
              borderColor={chip.borderColor}
              background={chip.background}
            />
          </Flex>
          <Flex alignItems={"baseline"} gap={"4px"} flexShrink={0}>
            {renderMetricLabel(
              "path_pick:search_products.size_stock_modal.table_stock"
            )}
            <Text textStyle={"body-sm-bold"} color={"#444440"}>
              {formatStock(row.stock)}
            </Text>
          </Flex>
        </Flex>

        {/* TODO: 사이즈별 공급가 데이터가 없어 상품 레벨 값 반복 */}
        <Flex alignItems={"baseline"} gap={"4px"}>
          {renderMetricLabel(
            "path_pick:search_products.size_stock_modal.table_supply_price"
          )}
          <Text textStyle={"body-sm-bold"} color={"#444440"}>
            {formatKrw(supplyPrice)}
          </Text>
        </Flex>

        <Grid
          templateColumns={MOBILE_METRIC_COLUMNS}
          gap={"8px 12px"}
          alignItems={"center"}
          padding={"10px"}
          borderRadius={"6px"}
          background={"#F7F9FA"}
        >
          <Box />
          {cells.map((cell) => (
            <Text
              key={cell.market}
              textStyle={"body-sm-bold"}
              color={"#444440"}
            >
              {PATH_PICK_MARKET_LABEL[cell.market]}
            </Text>
          ))}

          {renderMetricLabel(
            "path_pick:search_products.size_stock_modal.table_lowest_price"
          )}
          {cells.map((cell) => (
            <Box key={cell.market}>{renderLowestPrice(row, cell)}</Box>
          ))}

          {renderMetricLabel(
            "path_pick:search_products.size_stock_modal.table_last_price"
          )}
          {cells.map((cell) => (
            <Box key={cell.market}>{renderLastPrice(row, cell)}</Box>
          ))}

          {renderMetricLabel(
            "path_pick:search_products.size_stock_modal.table_sales_count"
          )}
          {cells.map((cell) => (
            <Box key={cell.market}>{renderSalesCount(cell)}</Box>
          ))}

          {renderMetricLabel(
            "path_pick:search_products.product_list.expected_margin"
          )}
          {cells.map((cell) => (
            <Box key={cell.market}>{renderMargin(row, cell)}</Box>
          ))}
        </Grid>

        <Flex alignItems={"flex-start"} gap={"8px"}>
          <Box flexShrink={0} paddingTop={"2px"}>
            {renderMetricLabel(
              "path_pick:search_products.size_stock_modal.table_recommendation"
            )}
          </Box>
          {renderRecommendation(row)}
        </Flex>
      </Flex>
    );
  };

  return (
    <Box overflowX={"auto"}>
      <Flex
        flexDirection={"column"}
        border={"0.5px solid #D4D4D1"}
        borderRadius={"8px"}
        overflow={"hidden"}
        // 열이 셋뿐인 표와 카드는 가로로 넘칠 일이 없다 — 최소 폭을 걸면 오히려 좁은 화면에서 스크롤이 생긴다.
        minWidth={
          isComparisonVisible && !isCardLayout ? TABLE_MIN_WIDTH : undefined
        }
      >
        {/* 카드는 칸마다 라벨을 직접 달고 있어 열 헤더가 필요 없다. */}
        {!isCardLayout && (
          <Grid
            templateColumns={tableColumns}
            gap={"8px"}
            background={"#F7F9FA"}
            padding={"10px 16px"}
          >
            {headerKeys.map((key) => (
              <Text
                key={key}
                textStyle={"body-sm-medium"}
                color={"#8F8F8C"}
                whiteSpace={"nowrap"}
              >
                {t(key)}
              </Text>
            ))}
          </Grid>
        )}

        {/* 조회 중에 실제 그룹을 그리면 아직 오지 않은 마켓이 잠깐 "미매칭"으로 보인다
            — 답이 오기 전에 없는 사실을 단정하는 셈이다. */}
        {isPending &&
          (isCardLayout ? (
            <OptionCardsSkeleton rowCount={pendingRowCount} />
          ) : (
            <OptionRowsSkeleton
              rowCount={pendingRowCount}
              columns={tableColumns}
              cellWidths={skeletonCellWidths}
            />
          ))}

        {!isPending &&
          model.groups.map((group) => {
            const theme = GROUP_THEMES[group.key];
            // 마켓 전용 그룹만 접었다 펼친다(패스레이드 옵션은 항상 보인다).
            const isCollapsible = group.key !== "pathrade";
            const isExpanded = Boolean(expandedGroups[group.key]);
            const isCollapsed = isCollapsible && !isExpanded;

            return (
              <React.Fragment key={group.key}>
                <Flex
                  padding={isCardLayout ? "8px 12px" : "8px 16px"}
                  gap={"6px"}
                  alignItems={"center"}
                  borderTop={"0.5px solid #D4D4D1"}
                  background={theme.background}
                  {...(isCollapsible && {
                    cursor: "pointer",
                    onClick: () =>
                      setExpandedGroups((prev) => ({
                        ...prev,
                        [group.key]: !prev[group.key],
                      })),
                  })}
                >
                  <Text textStyle={"body-sm-bold"} color={theme.color}>
                    {t(GROUP_TITLE_KEY[group.key], {
                      count: group.rows.length,
                    })}
                  </Text>
                  {isCollapsible && (
                    <>
                      <Text textStyle={"body-sm-medium"} color={theme.color}>
                        {t(
                          isExpanded
                            ? "path_pick:search_products.size_stock_modal.group_collapse"
                            : "path_pick:search_products.size_stock_modal.group_expand"
                        )}
                      </Text>
                      <CustomIcon
                        src={isExpanded ? "chevron-up" : "chevron-down"}
                        width={16}
                        height={16}
                        color={theme.color}
                      />
                    </>
                  )}
                </Flex>

                {/* 접힌 그룹은 행만 감춘다 — 그룹 자체를 빼면 다시 펼칠 손잡이까지 사라진다. */}
                {!isCollapsed &&
                  isCardLayout &&
                  group.rows.map(renderOptionCard)}

                {!isCollapsed &&
                  !isCardLayout &&
                  group.rows.map((row) => {
                    // 옵션 단위 값은 두 마켓 행에 걸쳐 한 칸으로 합친다.
                    // 비교를 끄면 하위 행이 없어 합칠 이유도 없다.
                    const spanProps = isComparisonVisible
                      ? ({ gridRow: "span 2" } as const)
                      : {};
                    const chip = isComparisonVisible
                      ? getMatchStateChip(row)
                      : null;

                    return (
                      <Grid
                        key={row.key}
                        templateColumns={tableColumns}
                        gap={"8px 8px"}
                        padding={"12px 16px"}
                        borderTop={"0.5px solid #D4D4D1"}
                        alignItems={"center"}
                      >
                        <Text
                          {...spanProps}
                          textStyle={"body-sm-bold"}
                          color={"#444440"}
                        >
                          {row.label}
                        </Text>
                        {chip && (
                          <Box {...spanProps}>
                            <OptionChip
                              label={chip.label}
                              color={chip.color}
                              borderColor={chip.borderColor}
                              background={chip.background}
                            />
                          </Box>
                        )}
                        <Text
                          {...spanProps}
                          textStyle={"body-sm-bold"}
                          color={"#444440"}
                        >
                          {formatStock(row.stock)}
                        </Text>
                        {/* TODO: 사이즈별 공급가 데이터가 없어 상품 레벨 값 반복 */}
                        <Text
                          {...spanProps}
                          textStyle={"body-sm-bold"}
                          color={"#444440"}
                        >
                          {formatKrw(supplyPrice)}
                        </Text>
                        {isComparisonVisible && (
                          <>
                            {renderMarketCells(row, row.kream)}
                            <Box {...spanProps}>
                              {renderRecommendation(row)}
                            </Box>
                            {renderMarketCells(row, row.poizon)}
                          </>
                        )}
                      </Grid>
                    );
                  })}
              </React.Fragment>
            );
          })}
      </Flex>
    </Box>
  );
}

export default PathPickProductOptionTable;
